import { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { Icon } from '@/components/Icon';

const MenuContext = createContext();

export const MenuProvider = ({ children }) => {
  const [menuState, setMenuState] = useState({
    isOpen: false,
    x: 0,
    y: 0,
    items: [],
    rightHanded: false
  });

  const openMenu = (x, y, items, rightHanded) => setMenuState({ isOpen: true, x, y, items, rightHanded });
  const closeMenu = () => setMenuState(prev => ({ ...prev, isOpen: false }));

  return (
    <MenuContext.Provider value={{ menuState, openMenu, closeMenu }}>
      {children}
      <Menu />
    </MenuContext.Provider>
  );
};

const Menu = () => {
  const { menuState, closeMenu } = useContext(MenuContext);
  const menuRef = useRef(null);
  const [focusIndex, setFocusIndex] = useState(0);
  const [returnFocusToElement, setReturnFocusToElement] = useState(null);

  const buttonItems = menuState.items.filter(item => item !== "separator");
  const enabledButtonItems = buttonItems.filter(item => !item.disabled);

  const handleKeyDown = useCallback((e) => {
    if (!menuState.isOpen) return;

    switch (e.key) {
      case 'Escape':
        e.preventDefault();
        closeMenu();
        break;
      case 'ArrowDown':
        e.preventDefault();
        if (enabledButtonItems.length > 0) {
          setFocusIndex((prevIndex) => {
            let newIndex = (prevIndex + 1) % enabledButtonItems.length;
            return newIndex;
          });
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (enabledButtonItems.length > 0) {
          setFocusIndex((prevIndex) => 
            (prevIndex - 1 + enabledButtonItems.length) % enabledButtonItems.length
          );
        }
        break;
      case 'Tab':
        e.preventDefault(); // Prevent tabbing out of the menu
        break;
    }
  }, [menuState.isOpen, enabledButtonItems.length, closeMenu]);

  useEffect(() => {
    if (menuState.isOpen) {
      setReturnFocusToElement(document.activeElement);
      menuRef.current?.focus();
    } else {
      returnFocusToElement?.focus();
      setFocusIndex(0);
    }

    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        closeMenu();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuState.isOpen, closeMenu]);

  useEffect(() => {
    if (menuState.isOpen && menuRef.current) {
      const menuItems = menuRef.current.querySelectorAll('[role="menuitem"]:not(.disabled)');
      menuItems[focusIndex]?.focus();
    }
  }, [focusIndex, menuState.isOpen]);

  if (!menuState.isOpen) return null;

  let menuPosition = {
    left: menuState.x - 1,
    top: menuState.y + 4,
  }

  let menuHeight = menuState.items.map(a => a == "separator" ? 11 : 42).reduce((a, b) => a + b, (5 + 5)) + 10; // 10 is just margin
  let menuWidth = 200;

  if (menuPosition.top + menuHeight > window.innerHeight) {
    menuPosition.bottom = window.innerHeight - menuState.y;
    delete menuPosition.top;
  }

  if (menuPosition.left + menuWidth > window.innerWidth || menuState.rightHanded) {
    menuPosition.right = window.innerWidth - menuPosition.left;
    delete menuPosition.left;
  }

  return (
    <ul
      ref={menuRef}
      role="menu"
      tabIndex={-1}
      onKeyDown={handleKeyDown}
      className="context_menu"
      style={menuPosition}
    >
      {menuState.items.map((item, index) => (
        item == "separator" ?
        <li key={index} className="context_menu_separator"></li> :
        <li
          key={index}
          role="menuitem"
          className={`context_menu_item ${item.disabled ? 'disabled' : ''}`}
          tabIndex={item.disabled ? undefined : -1}
          onClick={() => {
            if (item.disabled) return;
            item.onClick();
            closeMenu();
          }}
          aria-disabled={item.disabled}
        >
          <Icon name={item.icon} />
          <div>{item.label}</div>
        </li>
      ))}
    </ul>
  );
};

export const useMenu = ({ menuItems }) => {
  const menuContext = useContext(MenuContext);

  const handleContextMenu = (e) => {
    e.preventDefault();
    menuContext.openMenu(e.clientX, e.clientY, menuItems, false);
  };

  const handleOnClick = (e) => {
    let {x, y, height, width} = e.target.closest('button').getBoundingClientRect();
    e.preventDefault();
    e.stopPropagation();
    menuContext.openMenu(x+width, y+height-2, menuItems, true);
  }

  return {
    handleContextMenu,
    handleOnClick
  }
};